/**
 * The dashboard's local, unsaved answers about a position: whether a reviewer
 * toggled it in or out of leverage, and whether they put an unschedulable one
 * on a cadence by hand.
 *
 * Cadence and leverage stay separate here. A hand-picked schedule only changes
 * how the row is priced until the server answers; it does not flip the active
 * / in-leverage state on its own. The dashboard and FunderCard share these
 * helpers so keyed lookups cannot drift between the card and the figures.
 */

/**
 * How a withdrawal is identified in that state.
 *
 * Prefers the backend's override key, which survives the refetch a saved
 * correction triggers. The array index is the fallback for MoneyThumb output,
 * which has no key to offer — nothing there is ever saved or refetched, so the
 * index cannot shift underneath an override.
 */
export function withdrawalKey(w: { override_key?: string }, index: number): string {
  return w.override_key ?? String(index);
}

/**
 * Record or drop one withdrawal's entry, keyed funder-then-withdrawal.
 *
 * `undefined` deletes rather than stores, which is what restoring a failed save
 * needs: a position that had no override before the click has to end up with
 * none again, not with one pinning it to the value it already had.
 */
export function setOverride<T>(
  prev: Record<string, Record<string, T>>,
  funderKey: string,
  wKey: string,
  value: T | undefined
): Record<string, Record<string, T>> {
  const forFunder = { ...(prev[funderKey] || {}) };
  if (value === undefined) {
    delete forFunder[wKey];
  } else {
    forFunder[wKey] = value;
  }
  return { ...prev, [funderKey]: forFunder };
}

/**
 * Drop local leverage toggles that already match what the server is showing.
 *
 * After a successful persist the optimistic entry should go away so a later
 * correction from elsewhere (the CorrectionsPanel checkbox) is not outranked
 * by a stale local flip. Same cleanup runs when analysis catches up.
 */
export function pruneMatchingLeverageOverrides(
  prev: Record<string, Record<string, boolean>>,
  groups: Array<{
    funderKey: string;
    withdrawals: Array<{
      withdrawalKey: string;
      include_in_leverage?: boolean;
      is_active: boolean;
    }>;
  }>
): Record<string, Record<string, boolean>> {
  let next = prev;
  for (const group of groups) {
    for (const w of group.withdrawals) {
      const forFunder = next[group.funderKey];
      if (!forFunder || !(w.withdrawalKey in forFunder)) continue;
      const server = w.include_in_leverage ?? w.is_active;
      if (forFunder[w.withdrawalKey] !== server) continue;
      next = setOverride(next, group.funderKey, w.withdrawalKey, undefined);
    }
  }
  return next;
}
